<template>
  <div
    :class="[
      theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
      'border rounded-lg p-4'
    ]"
  >
    <div class="flex flex-wrap items-end gap-4">
      <!-- Search Input -->
      <div class="flex-1 min-w-[250px]">
        <label
          class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
        >
          <span class="text-blue-500">🔍</span> Search
        </label>
        <div class="relative">
          <input
            :value="filters.search"
            type="text"
            placeholder="Search teams, emails, IDs... (Ctrl+K)"
            class="w-full px-3 py-2 pr-8 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400"
            @input="updateSearch(($event.target as HTMLInputElement).value)"
          />
          <button
            v-if="filters.search"
            class="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center text-lg leading-none bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-blue-500 hover:text-white transition-all"
            title="Clear search"
            @click="clearSearch"
          >
            ×
          </button>
        </div>
      </div>

      <!-- Offset Input -->
      <div class="w-24">
        <label
          class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
        >
          <span class="text-blue-500">📍</span> Offset
        </label>
        <input
          :value="filters.offset"
          type="number"
          min="0"
          class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          @input="updateOffset(parseInt(($event.target as HTMLInputElement).value) || 0)"
          @keypress.enter="$emit('load-data')"
        />
      </div>

      <!-- Limit Input -->
      <div class="w-24">
        <label
          class="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5"
        >
          <span class="text-blue-500">📊</span> Limit
        </label>
        <input
          :value="filters.limit"
          type="number"
          min="1"
          max="200"
          class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          @input="updateLimit(parseInt(($event.target as HTMLInputElement).value) || 50)"
          @keypress.enter="$emit('load-data')"
        />
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-2">
        <button
          :disabled="loading"
          :class="[
            'px-5 py-2 rounded-md font-semibold shadow-sm transition-all flex items-center gap-2 text-white',
            loading
              ? 'bg-blue-400 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 active:scale-95'
          ]"
          @click="$emit('load-data')"
        >
          <svg
            :class="[loading ? 'animate-spin' : '', 'w-4 h-4']"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Load Data
        </button>
        <button
          class="px-5 py-2 rounded-md font-semibold shadow-sm transition-all bg-gray-500 hover:bg-gray-600 active:scale-95 text-white"
          @click="$emit('reset')"
        >
          ↺ Reset
        </button>
      </div>
    </div>

    <!-- Result Count Badge -->
    <div
      v-if="filters.search && resultCount !== undefined"
      class="mt-3"
    >
      <span
        class="inline-block px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 border border-blue-300 dark:border-blue-700"
      >
        {{ resultCount }} result{{ resultCount === 1 ? '' : 's' }} found
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject } from 'vue'
import { useTheme } from '../../../utils/useTheme'
import type { TaskSentinelFilters } from '../../../types/task-sentinel'

const theme = inject('theme', useTheme())

const props = defineProps<{
  filters: TaskSentinelFilters
  loading?: boolean
  resultCount?: number
}>()

const emit = defineEmits<{
  (e: 'update-search', value: string): void
  (e: 'update-offset', value: number): void
  (e: 'update-limit', value: number): void
  (e: 'load-data'): void
  (e: 'reset'): void
}>()

const updateSearch = (value: string) => {
  emit('update-search', value)
}

const updateOffset = (value: number) => {
  emit('update-offset', value)
}

const updateLimit = (value: number) => {
  emit('update-limit', value)
}

const clearSearch = () => {
  emit('update-search', '')
}
</script>
